import React, { useRef, useState } from 'react';
import {
  View,
  Text,
  Image,
  TouchableOpacity,
  StyleSheet,
  Dimensions,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import ViewShot from 'react-native-view-shot';
import * as Sharing from 'expo-sharing';
import * as FileSystem from 'expo-file-system';
import BottomSheet, { BottomSheetScrollView } from '@gorhom/bottom-sheet';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import ShareableMomentCard from './ShareableMomentCard';

const { width: screenWidth } = Dimensions.get('window');

const ShareBiteModal = ({ visible, onClose, moment, privyDid, onRestaurantPress, initialPhotoIndex = 0 }) => {
  const insets = useSafeAreaInsets();
  const viewShotRef = useRef(null);
  const bottomSheetRef = useRef(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [showDetails, setShowDetails] = useState(true);

  // Calculate absolute pixel values for snapPoints to prevent Reanimated warnings
  const screenHeight = Dimensions.get('window').height;
  const snapPoints = React.useMemo(() => [screenHeight * 0.95], [screenHeight]);

  React.useEffect(() => {
    if (visible) {
      bottomSheetRef.current?.expand();
    } else {
      bottomSheetRef.current?.close();
    }
  }, [visible]);

  const handleShare = async () => {
    if (!viewShotRef.current) return;

    setIsCapturing(true);
    try {
      // Capture the view as PNG
      const uri = await viewShotRef.current.capture();
      
      // Check if sharing is available
      const isAvailable = await Sharing.isAvailableAsync();
      if (!isAvailable) {
        Alert.alert('Error', 'Sharing is not available on this device');
        return;
      }

      // Share the image
      await Sharing.shareAsync(uri, {
        mimeType: 'image/png',
        dialogTitle: 'Share your bite!',
      });

      onClose();
    } catch (error) {
      console.error('Error sharing bite:', error);
      Alert.alert('Error', 'Failed to share bite. Please try again.');
    } finally {
      setIsCapturing(false);
    }
  };

  const handleClose = () => {
    if (!isCapturing) {
      bottomSheetRef.current?.close();
      setTimeout(() => onClose(), 300); // Wait for animation to complete
    }
  };

  if (!moment) return null;

  return (
    <BottomSheet
      ref={bottomSheetRef}
      index={visible ? 0 : -1}
      snapPoints={snapPoints}
      enablePanDownToClose={true}
      enableOverDrag={false}
      onClose={handleClose}
      backgroundStyle={styles.bottomSheetBackground}
      handleIndicatorStyle={styles.handleIndicator}
    >
      <BottomSheetScrollView
        style={styles.scroll}
        contentContainerStyle={[
          styles.scrollContent,
          { paddingBottom: 20 + insets.bottom },
        ]}
        showsVerticalScrollIndicator={true}
        keyboardShouldPersistTaps="handled"
        nestedScrollEnabled
      >
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>Share Bite</Text>
          <TouchableOpacity
            style={styles.closeButton}
            onPress={handleClose}
            disabled={isCapturing}
          >
            <Ionicons name="close" size={24} color="#8B8B8B" />
          </TouchableOpacity>
        </View>

        {/* Toggle Options */}
        <View style={styles.toggleContainer}>
          <TouchableOpacity
            style={[styles.toggleOption, showDetails && styles.toggleOptionActive]}
            onPress={() => setShowDetails(true)}
            disabled={isCapturing}
          >
            <Text style={[styles.toggleText, showDetails && styles.toggleTextActive]}>
              With Details
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.toggleOption, !showDetails && styles.toggleOptionActive]}
            onPress={() => setShowDetails(false)}
            disabled={isCapturing}
          >
            <Text style={[styles.toggleText, !showDetails && styles.toggleTextActive]}>
              Photo Only
            </Text>
          </TouchableOpacity>
        </View>

        {/* Full Screen Preview */}
        <View style={styles.previewContainer}>
          <ViewShot
            ref={viewShotRef}
            options={{
              fileName: `bite-${moment._id}`,
              format: 'png',
              quality: 1.0,
            }}
            style={styles.shotContainer}
          >
            <View style={styles.polaroidCard}>
              {/* Moment Card Content */}
              <View style={styles.momentContainer}>
                <ShareableMomentCard
                  moment={moment}
                  privyDid={privyDid}
                  onRestaurantPress={onRestaurantPress}
                  showDetails={showDetails}
                  initialPhotoIndex={initialPhotoIndex}
                />
              </View>

                {/* Polaroid Bottom Border */}
                <View style={styles.polaroidBottom}>
                  <Text style={styles.disgoText}>D I S G O</Text>
                </View>
            </View>
          </ViewShot>

          {/* Share Button - Right below polaroid */}
          <View style={styles.shareButtonContainer}>
            <TouchableOpacity
              style={[styles.shareButton, isCapturing && styles.shareButtonDisabled]}
              onPress={handleShare}
              disabled={isCapturing}
            >
              {isCapturing ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <>
                  <Ionicons name="share-outline" size={20} color="#FFFFFF" />
                  <Text style={styles.shareButtonText}>Share Bite</Text>
                </>
              )}
            </TouchableOpacity>
          </View>
        </View>

      </BottomSheetScrollView>
    </BottomSheet>
  );
};

const styles = StyleSheet.create({
  bottomSheetBackground: {
    backgroundColor: '#F5F1ED', // Light DISGO brown
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
  },
  handleIndicator: {
    backgroundColor: '#D4C4B8', // Slightly darker brown to complement the background
    width: 40,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 20,
    paddingTop: 20,
    flexGrow: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
    paddingBottom: 8,
    position: 'relative',
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1A1A1A',
  },
  closeButton: {
    padding: 4,
    position: 'absolute',
    right: 0,
  },
  toggleContainer: {
    flexDirection: 'row',
    backgroundColor: '#F5F5F5',
    borderRadius: 8,
    padding: 4,
    marginHorizontal: 20,
    marginBottom: 16,
    zIndex: 2,
    position: 'relative',
  },
  toggleOption: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignItems: 'center',
  },
  toggleOptionActive: {
    backgroundColor: 'white',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  toggleText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#8B8B8B',
  },
  toggleTextActive: {
    color: '#1A1A1A',
    fontWeight: '600',
  },
  previewContainer: {
    alignItems: 'center',
    justifyContent: 'flex-start',
    marginBottom: 20,
    paddingHorizontal: 10,
    paddingTop: 4,
    width: '100%',
  },
  shotContainer: {
    backgroundColor: 'transparent',
    width: '100%',
    alignItems: 'center',
  },
  shareButtonContainer: {
    alignItems: 'center',
    marginTop: 12,
    marginBottom: 8,
  },
  polaroidCard: {
    backgroundColor: 'white',
    borderRadius: 0,
    padding: 14,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 8,
    },
    shadowOpacity: 0.25,
    shadowRadius: 16,
    elevation: 16,
    width: '100%',
    maxWidth: screenWidth - 60, // Account for padding
    borderWidth: 3,
    borderColor: '#D4C4B8', // Light DISGO brown
  },
  momentContainer: {
    borderRadius: 12,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    overflow: 'hidden',
  },
  polaroidBottom: {
    height: 60,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 8,
    backgroundColor: 'white',
    borderBottomLeftRadius: 16,
    borderBottomRightRadius: 16,
  },
  disgoText: {
    fontSize: 24,
    fontWeight: '700',
    color: '#522504',
    letterSpacing: 4,
    textAlign: 'center',
  },
  shareButton: {
    backgroundColor: '#522504',
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 14,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  shareButtonDisabled: {
    backgroundColor: '#CCCCCC',
  },
  shareIcon: {
    marginRight: 8,
  },
  shareButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});

export default ShareBiteModal;
